/**
 * routes/cuenta.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Sprint: Account Value Layer V1 — Recent Searches, Wishlist + Saved
 * Comparisons. Redesigns Auth Foundation V1's single-section account page
 * into a real multi-section account: Overview, Profile, Site preferences,
 * Travel preferences, Recent searches, Wishlist, Saved comparisons, Help.
 *
 * Sprint: Account UX Refinement V1 — desktop now uses a real vertical left
 * sidebar (not a horizontal scroll-tab row) for a clearer hierarchy at
 * wider viewports; mobile keeps the horizontal scroll-tab bar, which is
 * already an accessible, compact pattern at narrow widths. Also adds a
 * lightweight, entirely-optional profile-completion form (display_name/
 * phone/home_country/preferred_traveler_type — supabase/migrations/022_
 * profiles_completion_fields.sql) and editable travel-interest chips
 * (travel_interests) — both are PROFILE preferences only; neither changes
 * marketplace search/filter behavior in this sprint.
 *
 * Unauthenticated / not-configured behavior is UNCHANGED from Auth
 * Foundation V1 (never a redirect, never a blocking guard) — only the
 * authenticated view grew. Every list/row here loads best-effort via
 * lib/auth/{accountProfile,recentSearches,wishlist,savedComparisons}.ts —
 * a failed load just means an empty/default section, never a page error.
 *
 * `tab` is a real URL search param (validateSearch below), not local-only
 * state — so AuthUserMenu's dropdown items (components/AuthPopover.tsx) can
 * deep-link straight into a specific section, with labels that match the
 * sidebar's own (Task 8's own "labels match sidebar labels" requirement).
 */
import { useEffect, useState, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Heart, Search, Columns3, Trash2, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth/AuthContext";
import { isSupabaseEnabled } from "@/lib/supabase";
import { usePreferences } from "@/lib/hooks/usePreferences";
import { useI18n } from "@/lib/i18n/useI18n";
import { AuthSignInPopover } from "@/components/AuthPopover";
import { FlagIcon } from "@/components/FlagIcon";
import { loadProfile, type TravelerProfile } from "@/lib/profile/profileStore";
import {
  loadAccountProfile, updateAccountPreferences, computeProfileCompletion,
  TRAVEL_INTEREST_KEYS, type AccountProfile, type PreferredTravelerType,
} from "@/lib/auth/accountProfile";
import { listRecentSearches, removeRecentSearch, type RecentSearchRow } from "@/lib/auth/recentSearches";
import { listSavedExperiences, removeExperience, type SavedExperienceRow } from "@/lib/auth/wishlist";
import { listSavedComparisons, removeSavedComparison, type SavedComparisonRow } from "@/lib/auth/savedComparisons";
import type { MessageKey } from "@/lib/i18n/messages";
// Sprint: Product Analytics V1 — account_tab_opened, reusing `surface` for
// the tab id (same field every other event already uses for its own
// UI-surface dimension). Never carries anything profile-shaped.
import { logMarketplaceEvent } from "@/lib/marketplaceEventLog";

export const Route = createFileRoute("/cuenta")({
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: AccountPage,
  head: () => ({
    meta: [
      { title: "Mi cuenta — Sift Trip" },
    ],
  }),
});

interface SelectOption { code: string; label: string; flagCode: string }

const CURRENCIES: SelectOption[] = [
  { code: "USD", label: "USD", flagCode: "us" },
  { code: "EUR", label: "EUR", flagCode: "eu" },
  { code: "COP", label: "COP", flagCode: "co" },
  { code: "BRL", label: "BRL", flagCode: "br" },
];

const LANGUAGES: SelectOption[] = [
  { code: "es", label: "Español",   flagCode: "co" },
  { code: "en", label: "English",   flagCode: "gb" },
  { code: "pt", label: "Português", flagCode: "br" },
];

const TRAVELER_TYPES: PreferredTravelerType[] = ["solo", "pareja", "familia", "amigos", "mayores"];

function OptionRow({
  options,
  selected,
  onSelect,
}: {
  options:  SelectOption[];
  selected: string;
  onSelect: (code: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = opt.code === selected;
        return (
          <button
            key={opt.code}
            type="button"
            onClick={() => onSelect(opt.code)}
            className="flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors"
            style={{
              border:     `1px solid ${active ? "oklch(0.120 0.040 258)" : "oklch(0.880 0.014 70 / 0.50)"}`,
              background: active ? "oklch(0.120 0.040 258)" : "transparent",
              color:      active ? "#FFFFFF" : "oklch(0.07 0.012 258)",
              minHeight:  "40px",
            }}
          >
            <FlagIcon code={opt.flagCode} />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Generic toggle-chip row — used by both preferred_traveler_type
 *  (single-select, click-again-to-clear) and travel_interests
 *  (multi-select). No FlagIcon dependency, unlike OptionRow above. */
function ChipRow({
  options,
  isSelected,
  onToggle,
}: {
  options:      ReadonlyArray<{ code: string; label: string }>;
  isSelected:   (code: string) => boolean;
  onToggle:     (code: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = isSelected(opt.code);
        return (
          <button
            key={opt.code}
            type="button"
            onClick={() => onToggle(opt.code)}
            aria-pressed={active}
            className="rounded-full px-3 py-2 text-sm font-medium transition-colors"
            style={{
              border:     `1px solid ${active ? "oklch(0.120 0.040 258)" : "oklch(0.880 0.014 70 / 0.50)"}`,
              background: active ? "oklch(0.120 0.040 258)" : "transparent",
              color:      active ? "#FFFFFF" : "oklch(0.07 0.012 258)",
              minHeight:  "36px",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const TABS = ["overview", "profile", "sitePrefs", "travelPrefs", "recent", "wishlist", "comparisons", "help"] as const;
type AccountTab = typeof TABS[number];

const TAB_LABEL_KEYS: Record<AccountTab, MessageKey> = {
  overview:     "account.tabs.overview",
  profile:      "account.tabs.profile",
  sitePrefs:    "account.tabs.sitePreferences",
  travelPrefs:  "account.tabs.travelPreferences",
  recent:       "account.tabs.recentSearches",
  wishlist:     "account.tabs.wishlist",
  comparisons:  "account.tabs.savedComparisons",
  help:         "account.tabs.help",
};

function isAccountTab(v: string | undefined): v is AccountTab {
  return TABS.includes(v as AccountTab);
}

function SectionCard({ children }: { children: ReactNode }) {
  return (
    <section
      className="rounded-2xl px-5 py-5 sm:px-6 sm:py-6"
      style={{ background: "oklch(0.978 0.010 72)", border: "1px solid oklch(0.880 0.024 72 / 0.45)" }}
    >
      {children}
    </section>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-serif font-bold text-lg mb-4" style={{ color: "oklch(0.07 0.012 258)" }}>
      {children}
    </h2>
  );
}

/** Sprint: Account UX Refinement V1 — every empty state now has a short
 *  title, a helpful body (the original, brief-mandated exact copy from
 *  Account Value Layer V1), and a clear CTA. No fake data anywhere. */
function EmptyState({ title, body, ctaLabel, ctaTo }: { title: string; body: string; ctaLabel: string; ctaTo: string }) {
  return (
    <div className="text-center py-8 px-4">
      <p className="font-bold text-sm mb-1" style={{ color: "oklch(0.07 0.012 258)" }}>{title}</p>
      <p className="text-sm mb-4" style={{ color: "oklch(0.38 0.016 258 / 0.55)" }}>{body}</p>
      <Link
        to={ctaTo}
        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
        style={{ background: "oklch(0.120 0.040 258)", minHeight: "40px" }}
      >
        <Search size={12} aria-hidden />
        {ctaLabel}
      </Link>
    </div>
  );
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

function SaveStatusNote({ status, savedLabel, errorLabel }: { status: SaveStatus; savedLabel: string; errorLabel: string }) {
  if (status === "saved") {
    return (
      <p className="inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: "oklch(0.46 0.12 145)" }}>
        <CheckCircle2 size={12} aria-hidden />
        {savedLabel}
      </p>
    );
  }
  if (status === "error") {
    return <p className="text-[12px] font-semibold" style={{ color: "oklch(0.55 0.20 25)" }}>{errorLabel}</p>;
  }
  return null;
}

function AccountPage() {
  const { user, loading, signOut } = useAuth();
  const { currency, language, setCurrency, setLanguage } = usePreferences();
  const { t } = useI18n();
  const { tab: rawTab } = Route.useSearch();
  const activeTab: AccountTab = isAccountTab(rawTab) ? rawTab : "overview";

  // Sprint: Product Analytics V1 — fires once per tab (including the
  // initial "overview"), never per render — the dependency array is
  // exactly [activeTab], so re-renders that don't change the tab (a
  // profile field edit, a preference change) never re-fire it.
  useEffect(() => {
    logMarketplaceEvent("account_tab_opened", { surface: activeTab });
  }, [activeTab]);

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(
        language === "en" ? "en-US" : language === "pt" ? "pt-BR" : "es-CO",
        { day: "numeric", month: "long", year: "numeric" },
      )
    : null;

  // Sprint: Account Value Layer V1 — every list loads once per logged-in
  // user, best-effort (each lib/auth/* function already never throws).
  const [recentSearches, setRecentSearches] = useState<RecentSearchRow[]>([]);
  const [wishlist, setWishlist]             = useState<SavedExperienceRow[]>([]);
  const [comparisons, setComparisons]       = useState<SavedComparisonRow[]>([]);
  const [travelerProfile, setTravelerProfile] = useState<TravelerProfile | null>(null);

  // Sprint: Account UX Refinement V1 — the Supabase-backed public.profiles
  // row (display_name/phone/home_country/preferred_traveler_type/
  // travel_interests), loaded once per user alongside the other lists.
  // Form fields are pre-filled directly from the load response (no
  // separate prefill effect/ref needed — this effect only ever runs once
  // per signed-in user, see the `[user]` dependency below).
  const [accountProfileRow, setAccountProfileRow] = useState<AccountProfile | null>(null);
  const [displayNameInput, setDisplayNameInput]   = useState("");
  const [phoneInput, setPhoneInput]               = useState("");
  const [homeCountryInput, setHomeCountryInput]   = useState("");
  const [travelerTypeInput, setTravelerTypeInput] = useState<PreferredTravelerType | null>(null);
  const [interestsInput, setInterestsInput]       = useState<Set<string>>(new Set());
  const [profileSaveStatus, setProfileSaveStatus]     = useState<SaveStatus>("idle");
  const [interestsSaveStatus, setInterestsSaveStatus] = useState<SaveStatus>("idle");

  useEffect(() => {
    if (!user) {
      setRecentSearches([]);
      setWishlist([]);
      setComparisons([]);
      setAccountProfileRow(null);
      return;
    }
    let cancelled = false;
    listRecentSearches(user.id).then(rows => { if (!cancelled) setRecentSearches(rows); });
    listSavedExperiences(user.id).then(rows => { if (!cancelled) setWishlist(rows); });
    listSavedComparisons(user.id).then(rows => { if (!cancelled) setComparisons(rows); });
    loadAccountProfile(user.id).then(row => {
      if (cancelled) return;
      setAccountProfileRow(row);
      setDisplayNameInput(row?.display_name ?? "");
      setPhoneInput(row?.phone ?? "");
      setHomeCountryInput(row?.home_country ?? "");
      setTravelerTypeInput(row?.preferred_traveler_type ?? null);
      setInterestsInput(new Set(row?.travel_interests ?? []));
    });
    return () => { cancelled = true; };
  }, [user]);

  // Local-only, no-auth traveler profile (lib/profile/profileStore.ts) — a
  // running tally of interests/pace/budget across sessions, unrelated to
  // (and never synced with) any Supabase table. Read once on mount; this
  // stays a read-only summary, shown alongside (never replaced by) the new
  // editable travel_interests chips above it.
  useEffect(() => {
    setTravelerProfile(loadProfile());
  }, []);

  const completion = computeProfileCompletion(accountProfileRow);

  async function handleRemoveRecentSearch(id: string) {
    if (!user) return;
    const ok = await removeRecentSearch(user.id, id);
    if (ok) setRecentSearches(prev => prev.filter(r => r.id !== id));
  }

  async function handleRemoveWishlistItem(row: SavedExperienceRow) {
    if (!user) return;
    const ok = await removeExperience(user.id, row.provider_key, row.provider_product_id);
    if (ok) setWishlist(prev => prev.filter(r => r.id !== row.id));
  }

  async function handleRemoveComparison(id: string) {
    if (!user) return;
    const ok = await removeSavedComparison(user.id, id);
    if (ok) setComparisons(prev => prev.filter(r => r.id !== id));
  }

  async function handleSaveProfile() {
    if (!user) return;
    setProfileSaveStatus("saving");
    const updates = {
      display_name:             displayNameInput.trim() || null,
      phone:                    phoneInput.trim() || null,
      home_country:             homeCountryInput.trim() || null,
      preferred_traveler_type:  travelerTypeInput,
    };
    const ok = await updateAccountPreferences(user.id, updates);
    setProfileSaveStatus(ok ? "saved" : "error");
    if (ok) setAccountProfileRow(prev => prev ? { ...prev, ...updates } : prev);
  }

  async function handleSaveInterests() {
    if (!user) return;
    setInterestsSaveStatus("saving");
    const travel_interests = Array.from(interestsInput);
    const ok = await updateAccountPreferences(user.id, { travel_interests });
    setInterestsSaveStatus(ok ? "saved" : "error");
    if (ok) setAccountProfileRow(prev => prev ? { ...prev, travel_interests } : prev);
  }

  return (
    <div className="min-h-screen max-w-5xl mx-auto px-5 py-16 sm:py-20">
      <h1
        className="font-serif font-black leading-none mb-6"
        style={{ fontSize: "clamp(1.7rem, 5vw, 2.3rem)", color: "oklch(0.07 0.012 258)", letterSpacing: "-0.02em" }}
      >
        {t("account.title")}
      </h1>

      {!isSupabaseEnabled ? (
        <p className="text-sm" style={{ color: "oklch(0.38 0.016 258 / 0.62)" }}>
          {t("account.notConfigured")}
        </p>
      ) : loading ? (
        <div className="space-y-3 animate-pulse">
          <div className="h-6 w-40 rounded-lg" style={{ background: "oklch(0.955 0.006 70)" }} />
          <div className="h-24 rounded-2xl" style={{ background: "oklch(0.955 0.006 70)" }} />
        </div>
      ) : !user ? (
        <div
          className="rounded-2xl px-6 py-8 text-center"
          style={{ background: "oklch(0.978 0.010 72)", border: "1px solid oklch(0.880 0.024 72 / 0.45)" }}
        >
          <p className="font-bold text-lg mb-2" style={{ color: "oklch(0.07 0.012 258)" }}>
            {t("account.loggedOutHeading")}
          </p>
          <p className="text-sm mb-6" style={{ color: "oklch(0.38 0.016 258 / 0.62)" }}>
            {t("account.loggedOutBody")}
          </p>
          <div className="flex justify-center">
            <AuthSignInPopover
              intent="signin"
              trigger={
                <button
                  type="button"
                  className="rounded-full px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ background: "oklch(0.120 0.040 258)", minHeight: "44px" }}
                >
                  {t("nav.signIn")}
                </button>
              }
            />
          </div>
        </div>
      ) : (
        // Sprint: Account UX Refinement V1 — desktop (md+) is a real 2-column
        // layout: a vertical sidebar (left, sticky) + content panel (right).
        // Below md, this collapses to a single column with the horizontal
        // scroll-tab bar (rendered inside the content column) taking the
        // sidebar's place — never two nav bars visible at once.
        <div className="md:flex md:items-start md:gap-10">
          {/* ── Desktop vertical sidebar ─────────────────────────────── */}
          <nav
            aria-label={t("account.title")}
            className="hidden md:flex md:flex-col md:w-56 md:shrink-0 gap-1 md:sticky md:top-24"
          >
            {TABS.map(tabId => {
              const active = tabId === activeTab;
              return (
                <Link
                  key={tabId}
                  to="/cuenta"
                  search={{ tab: tabId }}
                  aria-current={active ? "page" : undefined}
                  className="rounded-lg px-3.5 py-2.5 text-[13.5px] font-semibold text-left transition-colors"
                  style={{
                    background: active ? "oklch(0.120 0.040 258)" : "transparent",
                    color:      active ? "#FFFFFF" : "oklch(0.07 0.012 258)",
                    minHeight:  "40px",
                  }}
                >
                  {t(TAB_LABEL_KEYS[tabId])}
                </Link>
              );
            })}
          </nav>

          <div className="flex-1 min-w-0 space-y-6">
            {/* ── Mobile horizontal tab bar ──────────────────────────── */}
            <nav aria-label={t("account.title")} className="flex md:hidden gap-1.5 overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: "touch" }}>
              {TABS.map(tabId => {
                const active = tabId === activeTab;
                return (
                  <Link
                    key={tabId}
                    to="/cuenta"
                    search={{ tab: tabId }}
                    aria-current={active ? "page" : undefined}
                    className="shrink-0 rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors"
                    style={{
                      border:     `1px solid ${active ? "oklch(0.120 0.040 258)" : "oklch(0.880 0.014 70 / 0.50)"}`,
                      background: active ? "oklch(0.120 0.040 258)" : "transparent",
                      color:      active ? "#FFFFFF" : "oklch(0.07 0.012 258)",
                      minHeight:  "36px",
                    }}
                  >
                    {t(TAB_LABEL_KEYS[tabId])}
                  </Link>
                );
              })}
            </nav>

            {activeTab === "overview" && (
              <SectionCard>
                <SectionHeading>{t("account.overview.heading")}</SectionHeading>
                <p className="text-sm mb-5" style={{ color: "oklch(0.07 0.012 258)" }}>{user.email}</p>

                {/* Profile completion status */}
                <div className="mb-5">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <p className="text-xs font-semibold" style={{ color: "oklch(0.38 0.016 258 / 0.65)" }}>
                      {t("account.overview.completionHeading")}
                    </p>
                    <p className="text-xs font-bold" style={{ color: "oklch(0.07 0.012 258)" }}>
                      {t("account.overview.completionCount", { completed: String(completion.completedCount), total: String(completion.totalCount) })}
                    </p>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "oklch(0.880 0.024 72 / 0.35)" }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${completion.percent}%`, background: "oklch(0.120 0.040 258)" }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                  <Link to="/cuenta" search={{ tab: "recent" }} className="rounded-lg p-3 text-center transition-opacity hover:opacity-85" style={{ background: "oklch(0.998 0.004 76)", border: "1px solid oklch(0.880 0.024 72 / 0.38)" }}>
                    <Search size={16} aria-hidden className="mx-auto mb-1" style={{ color: "oklch(0.545 0.020 60 / 0.60)" }} />
                    <p className="text-[12px] font-semibold" style={{ color: "oklch(0.07 0.012 258)" }}>
                      {t("account.overview.recentSearchesCount", { count: String(recentSearches.length) })}
                    </p>
                  </Link>
                  <Link to="/cuenta" search={{ tab: "wishlist" }} className="rounded-lg p-3 text-center transition-opacity hover:opacity-85" style={{ background: "oklch(0.998 0.004 76)", border: "1px solid oklch(0.880 0.024 72 / 0.38)" }}>
                    <Heart size={16} aria-hidden className="mx-auto mb-1" style={{ color: "oklch(0.545 0.020 60 / 0.60)" }} />
                    <p className="text-[12px] font-semibold" style={{ color: "oklch(0.07 0.012 258)" }}>
                      {t("account.overview.wishlistCount", { count: String(wishlist.length) })}
                    </p>
                  </Link>
                  <Link to="/cuenta" search={{ tab: "comparisons" }} className="rounded-lg p-3 text-center transition-opacity hover:opacity-85" style={{ background: "oklch(0.998 0.004 76)", border: "1px solid oklch(0.880 0.024 72 / 0.38)" }}>
                    <Columns3 size={16} aria-hidden className="mx-auto mb-1" style={{ color: "oklch(0.545 0.020 60 / 0.60)" }} />
                    <p className="text-[12px] font-semibold" style={{ color: "oklch(0.07 0.012 258)" }}>
                      {t("account.overview.savedComparisonsCount", { count: String(comparisons.length) })}
                    </p>
                  </Link>
                </div>

                {/* Quick actions */}
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: "oklch(0.38 0.016 258 / 0.65)" }}>
                    {t("account.overview.quickActionsHeading")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ background: "oklch(0.120 0.040 258)", minHeight: "36px" }}
                    >
                      <Search size={12} aria-hidden />
                      {t("account.overview.quickAction.search")}
                    </Link>
                    <Link
                      to="/cuenta"
                      search={{ tab: "wishlist" }}
                      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12.5px] font-semibold transition-colors"
                      style={{ border: "1px solid oklch(0.880 0.014 70 / 0.50)", color: "oklch(0.07 0.012 258)", minHeight: "36px" }}
                    >
                      <Heart size={12} aria-hidden />
                      {t("account.overview.quickAction.wishlist")}
                    </Link>
                    <Link
                      to="/cuenta"
                      search={{ tab: "comparisons" }}
                      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12.5px] font-semibold transition-colors"
                      style={{ border: "1px solid oklch(0.880 0.014 70 / 0.50)", color: "oklch(0.07 0.012 258)", minHeight: "36px" }}
                    >
                      <Columns3 size={12} aria-hidden />
                      {t("account.overview.quickAction.comparisons")}
                    </Link>
                  </div>
                </div>
              </SectionCard>
            )}

            {activeTab === "profile" && (
              <SectionCard>
                <SectionHeading>{t("account.tabs.profile")}</SectionHeading>
                <p className="text-xs font-semibold mb-1" style={{ color: "oklch(0.38 0.016 258 / 0.55)" }}>{t("account.email")}</p>
                <p className="text-sm font-medium mb-3" style={{ color: "oklch(0.07 0.012 258)" }}>{user.email}</p>
                {memberSince && (
                  <p className="text-xs mb-5" style={{ color: "oklch(0.38 0.016 258 / 0.50)" }}>
                    {t("account.memberSince", { date: memberSince })}
                  </p>
                )}

                {/* Sprint: Account UX Refinement V1 — lightweight, entirely
                    optional profile-completion form. No sensitive fields
                    (no passport/ID/birthdate/payment) — see accountProfile.ts
                    and migration 022's own doc for the explicit exclusion. */}
                <div className="pt-4 mb-5" style={{ borderTop: "1px solid oklch(0.880 0.024 72 / 0.30)" }}>
                  <p className="font-bold text-sm mb-4" style={{ color: "oklch(0.07 0.012 258)" }}>
                    {t("account.profile.formHeading")}
                  </p>
                  <div className="space-y-4 max-w-md">
                    <div>
                      <label htmlFor="account-display-name" className="block text-xs font-semibold mb-1.5" style={{ color: "oklch(0.38 0.016 258 / 0.65)" }}>
                        {t("account.profile.displayNameLabel")}
                      </label>
                      <input
                        id="account-display-name"
                        type="text"
                        value={displayNameInput}
                        onChange={e => setDisplayNameInput(e.target.value)}
                        placeholder={t("account.profile.displayNamePlaceholder")}
                        className="w-full rounded-lg px-3 py-2.5 text-sm outline-none"
                        style={{ border: "1px solid oklch(0.880 0.014 70 / 0.50)", color: "oklch(0.07 0.012 258)", minHeight: "44px" }}
                      />
                    </div>
                    <div>
                      <label htmlFor="account-phone" className="block text-xs font-semibold mb-1.5" style={{ color: "oklch(0.38 0.016 258 / 0.65)" }}>
                        {t("account.profile.phoneLabel")}
                      </label>
                      <input
                        id="account-phone"
                        type="tel"
                        value={phoneInput}
                        onChange={e => setPhoneInput(e.target.value)}
                        placeholder={t("account.profile.phonePlaceholder")}
                        className="w-full rounded-lg px-3 py-2.5 text-sm outline-none"
                        style={{ border: "1px solid oklch(0.880 0.014 70 / 0.50)", color: "oklch(0.07 0.012 258)", minHeight: "44px" }}
                      />
                    </div>
                    <div>
                      <label htmlFor="account-home-country" className="block text-xs font-semibold mb-1.5" style={{ color: "oklch(0.38 0.016 258 / 0.65)" }}>
                        {t("account.profile.homeCountryLabel")}
                      </label>
                      <input
                        id="account-home-country"
                        type="text"
                        value={homeCountryInput}
                        onChange={e => setHomeCountryInput(e.target.value)}
                        placeholder={t("account.profile.homeCountryPlaceholder")}
                        className="w-full rounded-lg px-3 py-2.5 text-sm outline-none"
                        style={{ border: "1px solid oklch(0.880 0.014 70 / 0.50)", color: "oklch(0.07 0.012 258)", minHeight: "44px" }}
                      />
                    </div>
                    <div>
                      <p className="text-xs font-semibold mb-1.5" style={{ color: "oklch(0.38 0.016 258 / 0.65)" }}>
                        {t("account.profile.travelerTypeLabel")}
                      </p>
                      <ChipRow
                        options={TRAVELER_TYPES.map(v => ({ code: v, label: t(`account.travelerType.${v}` as MessageKey) }))}
                        isSelected={code => travelerTypeInput === code}
                        onToggle={code => setTravelerTypeInput(prev => prev === code ? null : code as PreferredTravelerType)}
                      />
                    </div>
                    <div className="flex items-center gap-3 pt-1">
                      <button
                        type="button"
                        onClick={() => void handleSaveProfile()}
                        disabled={profileSaveStatus === "saving"}
                        className="rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                        style={{ background: "oklch(0.120 0.040 258)", minHeight: "44px" }}
                      >
                        {profileSaveStatus === "saving" ? t("account.profile.saving") : t("account.profile.save")}
                      </button>
                      <SaveStatusNote status={profileSaveStatus} savedLabel={t("account.profile.saved")} errorLabel={t("account.profile.saveError")} />
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => void signOut()}
                  className="text-sm font-medium transition-opacity hover:opacity-70"
                  style={{ color: "oklch(0.55 0.20 25)", minHeight: "40px" }}
                >
                  {t("nav.signOut")}
                </button>
              </SectionCard>
            )}

            {activeTab === "sitePrefs" && (
              <SectionCard>
                <SectionHeading>{t("account.sitePreferences.heading")}</SectionHeading>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold mb-2" style={{ color: "oklch(0.38 0.016 258 / 0.65)" }}>{t("nav.language")}</p>
                    <OptionRow options={LANGUAGES} selected={language} onSelect={setLanguage} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold mb-2" style={{ color: "oklch(0.38 0.016 258 / 0.65)" }}>{t("nav.currency")}</p>
                    <OptionRow options={CURRENCIES} selected={currency} onSelect={setCurrency} />
                  </div>
                </div>
              </SectionCard>
            )}

            {activeTab === "travelPrefs" && (
              <SectionCard>
                <SectionHeading>{t("account.travelPreferences.heading")}</SectionHeading>

                {/* Sprint: Account UX Refinement V1 — editable interest
                    chips, a PROFILE preference only (saved to public.
                    profiles.travel_interests). Never applied to marketplace
                    search/filter behavior in this sprint. */}
                <div className="mb-6">
                  <p className="font-bold text-sm mb-1" style={{ color: "oklch(0.07 0.012 258)" }}>
                    {t("account.travelPreferences.interestsHeading")}
                  </p>
                  <p className="text-xs mb-3" style={{ color: "oklch(0.38 0.016 258 / 0.55)" }}>
                    {t("account.travelPreferences.interestsBody")}
                  </p>
                  <ChipRow
                    options={TRAVEL_INTEREST_KEYS.map(key => ({ code: key, label: t(`account.interest.${key}` as MessageKey) }))}
                    isSelected={code => interestsInput.has(code)}
                    onToggle={code => setInterestsInput(prev => {
                      const next = new Set(prev);
                      if (next.has(code)) next.delete(code); else next.add(code);
                      return next;
                    })}
                  />
                  <div className="flex items-center gap-3 pt-3">
                    <button
                      type="button"
                      onClick={() => void handleSaveInterests()}
                      disabled={interestsSaveStatus === "saving"}
                      className="rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                      style={{ background: "oklch(0.120 0.040 258)", minHeight: "44px" }}
                    >
                      {interestsSaveStatus === "saving" ? t("account.travelPreferences.saving") : t("account.travelPreferences.save")}
                    </button>
                    <SaveStatusNote status={interestsSaveStatus} savedLabel={t("account.travelPreferences.saved")} errorLabel={t("account.travelPreferences.saveError")} />
                  </div>
                </div>

                {/* Existing V1 read-only summary — kept as-is, just moved
                    below the new editable section, separated by a hairline
                    divider (never a second nested card). */}
                <div className="pt-5" style={{ borderTop: "1px solid oklch(0.880 0.024 72 / 0.30)" }}>
                  <p className="font-bold text-sm mb-3" style={{ color: "oklch(0.07 0.012 258)" }}>
                    {t("account.travelPreferences.learnedHeading")}
                  </p>
                  {!travelerProfile || travelerProfile.generation_count === 0 ? (
                    <p className="text-sm" style={{ color: "oklch(0.38 0.016 258 / 0.55)" }}>{t("account.travelPreferences.empty")}</p>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-xs" style={{ color: "oklch(0.38 0.016 258 / 0.55)" }}>{t("account.travelPreferences.body")}</p>
                      <p className="text-sm font-semibold" style={{ color: "oklch(0.07 0.012 258)" }}>
                        {t("account.travelPreferences.generationCount", { count: String(travelerProfile.generation_count) })}
                      </p>
                      <p className="text-sm" style={{ color: "oklch(0.07 0.012 258)" }}>
                        {t("account.travelPreferences.citiesExplored", { count: String(travelerProfile.cities_explored.length) })}
                      </p>
                      {Object.keys(travelerProfile.interest_counts).length > 0 && (
                        <div>
                          <p className="text-xs font-semibold mb-1.5" style={{ color: "oklch(0.38 0.016 258 / 0.65)" }}>
                            {t("account.travelPreferences.topInterests")}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(travelerProfile.interest_counts)
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 6)
                              .map(([interest]) => (
                                <span
                                  key={interest}
                                  className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                                  style={{ background: "oklch(0.998 0.004 76)", border: "1px solid oklch(0.880 0.024 72 / 0.38)", color: "oklch(0.07 0.012 258)" }}
                                >
                                  {interest}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {activeTab === "recent" && (
              <SectionCard>
                <SectionHeading>{t("account.recentSearches.heading")}</SectionHeading>
                {recentSearches.length === 0 ? (
                  <EmptyState
                    title={t("account.recentSearches.emptyTitle")}
                    body={t("account.recentSearches.empty")}
                    ctaLabel={t("account.recentSearches.emptyCta")}
                    ctaTo="/"
                  />
                ) : (
                  <ul className="space-y-2">
                    {recentSearches.map(row => (
                      <li
                        key={row.id}
                        className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5"
                        style={{ background: "oklch(0.998 0.004 76)", border: "1px solid oklch(0.880 0.024 72 / 0.38)" }}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: "oklch(0.07 0.012 258)" }}>
                            {row.destination_name || row.query || row.destination_iata || "—"}
                          </p>
                          <p className="text-[11px]" style={{ color: "oklch(0.38 0.016 258 / 0.50)" }}>
                            {new Date(row.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Link
                            to="/experiences"
                            search={{
                              destination_iata: row.destination_iata ?? undefined,
                              destination_name: row.destination_name ?? undefined,
                              q:                 row.query ?? undefined,
                            }}
                            className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[12px] font-semibold transition-opacity hover:opacity-85"
                            style={{ background: "oklch(0.120 0.040 258)", color: "#FFFFFF", minHeight: "32px" }}
                          >
                            <Search size={11} aria-hidden />
                            {t("account.recentSearches.rerun")}
                          </Link>
                          <button
                            type="button"
                            onClick={() => void handleRemoveRecentSearch(row.id)}
                            aria-label={t("account.recentSearches.removeAria", { query: row.destination_name || row.query || "" })}
                            className="inline-flex items-center justify-center rounded-full transition-opacity hover:opacity-70"
                            style={{ width: "32px", height: "32px", color: "oklch(0.545 0.020 60 / 0.60)" }}
                          >
                            <Trash2 size={13} aria-hidden />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            )}

            {activeTab === "wishlist" && (
              <SectionCard>
                <SectionHeading>{t("account.wishlist.heading")}</SectionHeading>
                {wishlist.length === 0 ? (
                  <EmptyState
                    title={t("account.wishlist.emptyTitle")}
                    body={t("account.wishlist.empty")}
                    ctaLabel={t("account.wishlist.emptyCta")}
                    ctaTo="/"
                  />
                ) : (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {wishlist.map(row => (
                      <li
                        key={row.id}
                        className="flex gap-3 rounded-lg p-2.5"
                        style={{ background: "oklch(0.998 0.004 76)", border: "1px solid oklch(0.880 0.024 72 / 0.38)" }}
                      >
                        <div className="shrink-0 rounded-lg overflow-hidden" style={{ width: "56px", height: "56px", background: "oklch(0.960 0.010 72)" }}>
                          {row.image_url && (
                            <img src={row.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-bold leading-snug line-clamp-2" style={{ color: "oklch(0.07 0.012 258)" }}>
                            {row.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {row.booking_url && (
                              <a
                                href={row.booking_url}
                                target="_blank"
                                rel="noopener noreferrer sponsored"
                                className="inline-flex items-center gap-1 text-[11px] font-semibold transition-opacity hover:opacity-75"
                                style={{ color: "oklch(0.120 0.040 258)" }}
                              >
                                {t("account.wishlist.viewProvider")}
                                <ArrowUpRight size={10} aria-hidden />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => void handleRemoveWishlistItem(row)}
                              aria-label={t("account.wishlist.removeAria", { itemTitle: row.title })}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold transition-opacity hover:opacity-75"
                              style={{ color: "oklch(0.545 0.020 60 / 0.60)" }}
                            >
                              <Trash2 size={10} aria-hidden />
                              {t("account.wishlist.remove")}
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            )}

            {activeTab === "comparisons" && (
              <SectionCard>
                <SectionHeading>{t("account.savedComparisons.heading")}</SectionHeading>
                {comparisons.length === 0 ? (
                  <EmptyState
                    title={t("account.savedComparisons.emptyTitle")}
                    body={t("account.savedComparisons.empty")}
                    ctaLabel={t("account.savedComparisons.emptyCta")}
                    ctaTo="/"
                  />
                ) : (
                  <ul className="space-y-3">
                    {comparisons.map(row => (
                      <li
                        key={row.id}
                        className="rounded-lg p-3"
                        style={{ background: "oklch(0.998 0.004 76)", border: "1px solid oklch(0.880 0.024 72 / 0.38)" }}
                      >
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate" style={{ color: "oklch(0.07 0.012 258)" }}>
                              {row.destination_name || row.query || "—"}
                            </p>
                            <p className="text-[11px]" style={{ color: "oklch(0.38 0.016 258 / 0.50)" }}>
                              {t("account.savedComparisons.itemCount", { count: String(row.item_count) })} · {new Date(row.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => void handleRemoveComparison(row.id)}
                            aria-label={t("account.savedComparisons.removeAria")}
                            className="inline-flex items-center justify-center rounded-full shrink-0 transition-opacity hover:opacity-70"
                            style={{ width: "32px", height: "32px", color: "oklch(0.545 0.020 60 / 0.60)" }}
                          >
                            <Trash2 size={13} aria-hidden />
                          </button>
                        </div>
                        {/* Escape hatch per the Account Value Layer V1 brief —
                            "si restaurar es demasiado riesgoso, muestra las
                            cards del snapshot y los CTAs del proveedor
                            solamente." No restore-into-live-comparison here. */}
                        {row.comparison_snapshot && row.comparison_snapshot.length > 0 && (
                          <div className="flex gap-2 overflow-x-auto pb-1">
                            {row.comparison_snapshot.map((item, idx) => (
                              <div
                                key={`${item.provider_key}-${item.provider_product_id}-${idx}`}
                                className="shrink-0 rounded-lg p-2"
                                style={{ width: "150px", background: "oklch(0.994 0.004 76)", border: "1px solid oklch(0.880 0.024 72 / 0.30)" }}
                              >
                                {item.image_url && (
                                  <div className="rounded overflow-hidden mb-1.5" style={{ aspectRatio: "4/3", background: "oklch(0.960 0.010 72)" }}>
                                    <img src={item.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                                  </div>
                                )}
                                <p className="text-[11px] font-bold leading-snug line-clamp-2" style={{ color: "oklch(0.07 0.012 258)" }}>
                                  {item.title}
                                </p>
                                {item.booking_url && (
                                  <a
                                    href={item.booking_url}
                                    target="_blank"
                                    rel="noopener noreferrer sponsored"
                                    className="inline-flex items-center gap-1 text-[10px] font-semibold mt-1 transition-opacity hover:opacity-75"
                                    style={{ color: "oklch(0.120 0.040 258)" }}
                                  >
                                    {t("account.wishlist.viewProvider")}
                                    <ArrowUpRight size={9} aria-hidden />
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            )}

            {activeTab === "help" && (
              <SectionCard>
                <SectionHeading>{t("account.help.heading")}</SectionHeading>
                <p className="text-sm mb-4" style={{ color: "oklch(0.38 0.016 258 / 0.62)" }}>
                  {t("account.help.body")}
                </p>
                <Link
                  to="/contacto"
                  className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ background: "oklch(0.120 0.040 258)", minHeight: "44px" }}
                >
                  {t("account.help.cta")}
                  <ArrowUpRight size={13} aria-hidden />
                </Link>
              </SectionCard>
            )}

            <div>
              <Link
                to="/"
                className="text-sm font-medium transition-opacity hover:opacity-70"
                style={{ color: "oklch(0.38 0.016 258 / 0.60)" }}
              >
                {t("account.backHome")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
